import { useLingui } from '@lingui/react/macro';
import { createPortal } from 'react-dom';

import { usePortalContainer } from '@/api/evo';
import { useUser } from '@/lib/config/use-user';

import { FeatureFeedback } from './feature-feedback';

/**
 * Persistent feedback tab (F2 / D28) — fixed-position widget always
 * visible on the Template Builder pages. Routes through the Evo
 * `evo-feature-feedback-collector` OC component in collapsible mode
 * so the user can minimise it but always reach it.
 *
 * Renders nothing until the host user is authenticated; otherwise
 * the OC component would receive empty `customerName` / `userId`
 * params and the feedback wouldn't be attributable.
 *
 * Portalled to `document.body` (or the host's configured portal
 * container) rather than rendered inline: it's positioned with
 * `fixed`, which is trapped by the nearest ancestor that establishes a
 * containing block (e.g. any `transform`), not necessarily the
 * viewport. Left inline, a host page (e.g. WFM) can end up rendering it
 * in the wrong place instead of the bottom-right corner of the screen.
 */
export function PersistentFeedbackTab() {
  const { t } = useLingui();
  const user = useUser();
  const portalContainer = usePortalContainer();
  if (!user) return null;
  return createPortal(
    <div className="fixed right-4 bottom-4 z-[9999]">
      <FeatureFeedback
        feature="RotaTemplates"
        product="RotareadyTemplateBuilder"
        customerName={user.organisation.id}
        userId={user.id}
        theme="light"
        reDisplayAfterDays={30}
        collapsible="true"
        label={t`How easy is Rota Templates to use?`}
      />
    </div>,
    portalContainer ?? document.body,
  );
}
